import React, { useState } from 'react';
import { 
  Building2, Users, GraduationCap, LayoutDashboard, 
  ArrowLeft, ShieldCheck, Lock, ExternalLink, Sparkles,
  Layers, LogOut, Key, ShieldAlert, CheckCircle2, AlertCircle
} from 'lucide-react';
import { DirectorDashboard } from './DirectorDashboard';
import { GlobalContentManagerDashboard } from './GlobalContentManagerDashboard';
import { FacultyDashboard } from './FacultyDashboard';
import { StudentDashboard } from './StudentDashboard';
import { AUTH_CREDENTIALS, USER_ROLES } from '../../data/lmsAuthData';

export const LMSContainer = ({ 
  initialRole = 'director', 
  selectedStudentId = 'student-dba', 
  onSelectStudent,
  setActiveView,
  authenticatedUser = null,
  onLogout = null
}) => {
  // Determine user from authenticatedUser prop or default credential
  const defaultUser = AUTH_CREDENTIALS.find(c => c.role === initialRole) || AUTH_CREDENTIALS[0];
  const activeUser = authenticatedUser || defaultUser;

  const [currentRoleView, setCurrentRoleView] = useState(
    initialRole === 'director' ? USER_ROLES.DIRECTOR :
    initialRole === 'content-manager' ? USER_ROLES.CONTENT_MANAGER :
    initialRole === 'faculty' ? USER_ROLES.FACULTY : USER_ROLES.STUDENT
  );

  const [accessDeniedNotice, setAccessDeniedNotice] = useState(null);

  // Role-Based Access Control & Least Privilege Enforcer
  const handleRoleViewChange = (targetRole) => {
    // Check if the active user has privilege to access this view
    const isGlobalAdmin = activeUser.role === USER_ROLES.DIRECTOR || activeUser.role === USER_ROLES.CONTENT_MANAGER;

    if (isGlobalAdmin) {
      // Global Admins (Director & LMS Content Manager) have master oversight
      setCurrentRoleView(targetRole);
      setAccessDeniedNotice(null);
    } else if (activeUser.role === targetRole) {
      // User accessing their own role
      setCurrentRoleView(targetRole);
      setAccessDeniedNotice(null);
    } else {
      // Least Privilege Access Denied
      setAccessDeniedNotice({
        attemptedRole: targetRole === USER_ROLES.DIRECTOR ? "Executive Director Console" :
                       targetRole === USER_ROLES.CONTENT_MANAGER ? "Global Content Manager Console" :
                       targetRole === USER_ROLES.FACULTY ? "Executive Faculty Portal" : "Student LMS",
        currentRoleTitle: activeUser.label,
        reason: `Under the Principle of Least Privilege, your account (${activeUser.label}) is restricted strictly to your assigned academic scope. Administrative and financial consoles require Tier 1 Global Admin credentials.`
      });
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 py-6 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Navigation & Portal Top Bar in Business School Navy & Gold */}
        <div className="bg-white rounded-3xl p-4 sm:p-5 border-2 border-slate-200 shadow-sm flex flex-wrap items-center justify-between gap-4">
          
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setActiveView('home')}
              className="flex items-center space-x-2 text-xs font-bold text-[#0B2644] hover:text-white bg-amber-50 hover:bg-[#7A470E] px-4 py-2.5 rounded-xl border border-[#7A470E]/40 transition-colors cursor-pointer font-sc shadow-2xs"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Public Portal</span>
            </button>

            {/* Authenticated User Status Chip */}
            <div className="flex items-center space-x-2.5 bg-slate-50 px-3.5 py-1.5 rounded-2xl border border-slate-200">
              <div className="relative">
                <img 
                  src={activeUser.avatar} 
                  alt={activeUser.name} 
                  className="w-8 h-8 rounded-full object-cover border border-[#7A470E]"
                />
                <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 rounded-full border border-white" />
              </div>
              <div className="text-left">
                <div className="flex items-center space-x-1.5">
                  <span className="text-xs font-bold text-slate-900 font-sc">{activeUser.name}</span>
                  <span className={`text-[9px] font-black px-1.5 py-0.2 rounded font-mono ${
                    activeUser.role === USER_ROLES.DIRECTOR ? 'bg-red-100 text-red-900' :
                    activeUser.role === USER_ROLES.CONTENT_MANAGER ? 'bg-emerald-100 text-emerald-900' :
                    activeUser.role === USER_ROLES.FACULTY ? 'bg-amber-100 text-amber-900' :
                    'bg-blue-100 text-blue-900'
                  }`}>
                    {activeUser.role === USER_ROLES.DIRECTOR ? 'DIR · GLOBAL ADMIN' :
                     activeUser.role === USER_ROLES.CONTENT_MANAGER ? 'LMS · GLOBAL ADMIN' :
                     activeUser.role === USER_ROLES.FACULTY ? 'SCOPED FACULTY' : 'SCHOLAR'}
                  </span>
                </div>
                <div className="text-[10px] text-slate-500 font-mono truncate max-w-[200px] sm:max-w-xs">
                  {activeUser.leastPrivilegeTier}
                </div>
              </div>
            </div>
          </div>

          {/* Quick Sign Out / Change Persona Button */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                if (onLogout) {
                  onLogout();
                } else {
                  setActiveView('lms-login');
                }
              }}
              className="flex items-center space-x-1.5 text-xs font-bold text-slate-700 hover:text-red-700 bg-slate-100 hover:bg-red-50 px-3 py-2 rounded-xl border border-slate-200 transition-colors cursor-pointer font-sc"
              title="Sign in with different credentials"
            >
              <LogOut className="w-3.5 h-3.5 text-slate-500 hover:text-red-700" />
              <span className="hidden sm:inline">Change Credentials / Sign Out</span>
            </button>
          </div>

        </div>

        {/* Unified Role Switcher with Least Privilege Guards */}
        <div className="bg-white rounded-2xl p-2.5 border-2 border-slate-200 shadow-sm flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-1.5 text-xs font-sc">
            <span className="text-slate-500 font-bold px-2 hidden md:inline">Console View:</span>
            
            {/* 1. Director's Console */}
            <button
              onClick={() => handleRoleViewChange(USER_ROLES.DIRECTOR)}
              className={`px-3.5 py-2 rounded-xl font-bold transition-all cursor-pointer flex items-center space-x-1.5 ${
                currentRoleView === USER_ROLES.DIRECTOR
                  ? 'bg-[#0B2644] text-white shadow-xs'
                  : 'text-slate-700 hover:text-[#0B2644] hover:bg-slate-100'
              }`}
            >
              <Building2 className={`w-3.5 h-3.5 ${currentRoleView === USER_ROLES.DIRECTOR ? 'text-amber-300' : ''}`} />
              <span>Director's Console</span>
              {activeUser.role !== USER_ROLES.DIRECTOR && activeUser.role !== USER_ROLES.CONTENT_MANAGER && (
                <Lock className="w-3 h-3 text-slate-400 ml-1" />
              )}
            </button>

            {/* 2. Global Content Manager Console */}
            <button
              onClick={() => handleRoleViewChange(USER_ROLES.CONTENT_MANAGER)}
              className={`px-3.5 py-2 rounded-xl font-bold transition-all cursor-pointer flex items-center space-x-1.5 ${
                currentRoleView === USER_ROLES.CONTENT_MANAGER
                  ? 'bg-[#0B2644] text-white shadow-xs'
                  : 'text-slate-700 hover:text-[#0B2644] hover:bg-slate-100'
              }`}
            >
              <Layers className={`w-3.5 h-3.5 ${currentRoleView === USER_ROLES.CONTENT_MANAGER ? 'text-amber-300' : ''}`} />
              <span>LMS Global Content Manager</span>
              {activeUser.role !== USER_ROLES.DIRECTOR && activeUser.role !== USER_ROLES.CONTENT_MANAGER && (
                <Lock className="w-3 h-3 text-slate-400 ml-1" />
              )}
            </button>

            {/* 3. Faculty Portal */}
            <button
              onClick={() => handleRoleViewChange(USER_ROLES.FACULTY)}
              className={`px-3.5 py-2 rounded-xl font-bold transition-all cursor-pointer flex items-center space-x-1.5 ${
                currentRoleView === USER_ROLES.FACULTY
                  ? 'bg-[#7A470E] text-white shadow-xs'
                  : 'text-slate-700 hover:text-[#0B2644] hover:bg-slate-100'
              }`}
            >
              <Users className="w-3.5 h-3.5" />
              <span>Faculty Portal</span>
              {activeUser.role === USER_ROLES.STUDENT && (
                <Lock className="w-3 h-3 text-slate-400 ml-1" />
              )}
            </button>

            {/* 4. Student LMS */}
            <button
              onClick={() => handleRoleViewChange(USER_ROLES.STUDENT)}
              className={`px-3.5 py-2 rounded-xl font-bold transition-all cursor-pointer flex items-center space-x-1.5 ${
                currentRoleView === USER_ROLES.STUDENT
                  ? 'bg-[#0B2644] text-white shadow-xs border border-[#7A470E]/50'
                  : 'text-slate-700 hover:text-[#0B2644] hover:bg-slate-100'
              }`}
            >
              <GraduationCap className="w-3.5 h-3.5" />
              <span>Student LMS</span>
            </button>
          </div>

          <div className="hidden lg:flex items-center space-x-2 text-xs font-mono text-slate-600 font-bold">
            <Lock className="w-3.5 h-3.5 text-[#7A470E]" />
            <span>RBAC Protected · TLS 256-Bit</span>
          </div>
        </div>

        {/* Least Privilege Access Denied Notice Modal / Banner */}
        {accessDeniedNotice && (
          <div className="p-4 rounded-2xl bg-amber-50 border-2 border-amber-300 text-slate-900 shadow-md flex items-start justify-between gap-4 animate-in fade-in duration-150">
            <div className="flex items-start space-x-3">
              <ShieldAlert className="w-5 h-5 text-[#7A470E] shrink-0 mt-0.5" />
              <div>
                <h4 className="font-bold font-sc text-sm text-[#0B2644]">
                  Least Privilege Access Notice: {accessDeniedNotice.attemptedRole}
                </h4>
                <p className="text-xs text-slate-700 mt-0.5 font-sans leading-relaxed">
                  {accessDeniedNotice.reason}
                </p>
                <div className="text-[11px] text-[#7A470E] font-bold font-mono mt-1">
                  Active Security Profile: {accessDeniedNotice.currentRoleTitle}
                </div>
              </div>
            </div>
            <button
              onClick={() => setAccessDeniedNotice(null)}
              className="text-xs font-bold text-slate-500 hover:text-slate-900 px-2 py-1 rounded bg-white border border-slate-300 font-sc"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Dynamic Role Views */}
        {currentRoleView === USER_ROLES.DIRECTOR && <DirectorDashboard />}
        {currentRoleView === USER_ROLES.CONTENT_MANAGER && <GlobalContentManagerDashboard />}
        {currentRoleView === USER_ROLES.FACULTY && <FacultyDashboard />}
        {currentRoleView === USER_ROLES.STUDENT && (
          <StudentDashboard 
            selectedStudentId={selectedStudentId} 
            onSelectStudent={onSelectStudent} 
          />
        )}

      </div>
    </div>
  );
};
